import { useState, type ReactNode, useEffect, useRef, type JSX } from 'react';
import { useIntl } from '@doxyme/locale';
import { useTheme } from '../../../../theme';
import { Caption } from '../../../../typography';
import { useFormMessageStyles } from './formMessage.styles';
import Box from '@mui/material/Box';

const SHOW_CHARACTERS_THRESHOLD = 5;

export type FormMessageProps = {
  helperText?: ReactNode;
  errorText?: ReactNode;
  inputLength?: number;
  inputMaxLength?: number | undefined;
  /**
   * In some cases e.g. Text Area we always want to show the character count
   * from the start and never show the helper text.
   * @default false
   */
  alwaysShowCharacterCount?: boolean;
  message?: string;
};

export const isShowingCharCounter = ({
  inputMaxLength = 0,
  alwaysShowCharacterCount = false,
  inputLength = 0,
}: Pick<
  FormMessageProps,
  'inputLength' | 'inputMaxLength' | 'alwaysShowCharacterCount'
>): boolean =>
  inputMaxLength > 0 &&
  (alwaysShowCharacterCount ||
    inputLength >= inputMaxLength - SHOW_CHARACTERS_THRESHOLD);

export const shouldRenderFormMessage = ({
  errorText,
  helperText,
  inputMaxLength = 0,
  alwaysShowCharacterCount = false,
  inputLength = 0,
}: FormMessageProps): boolean => {
  return (
    Boolean(errorText) ||
    Boolean(helperText) ||
    isShowingCharCounter({
      inputMaxLength,
      alwaysShowCharacterCount,
      inputLength,
    })
  );
};

export const FormMessage = ({
  helperText,
  errorText,
  inputLength = 0,
  inputMaxLength = 0,
  alwaysShowCharacterCount = false,
  message,
}: FormMessageProps): JSX.Element | null => {
  const styles = useFormMessageStyles();
  const theme = useTheme();
  const { formatMessage } = useIntl();

  /**
   * Enable the character count pulse animation only when inputLength has changed as
   * it is basically supposed to be a reaction to inputLength changes.
   */
  const isCharacterCountPulseEnabled = useRef(false);
  const [originalInputLength] = useState(inputLength);

  useEffect(() => {
    if (inputLength !== originalInputLength) {
      isCharacterCountPulseEnabled.current = true;
    }
  }, [inputLength, originalInputLength]);

  if (
    !shouldRenderFormMessage({
      errorText,
      helperText,
      inputLength,
      inputMaxLength,
      alwaysShowCharacterCount,
    })
  ) {
    return null;
  }

  // If `maxLength` is provided & the number of characters is within the threshold
  const isShowCharCounter = isShowingCharCounter({
    inputMaxLength,
    alwaysShowCharacterCount,
    inputLength,
  });

  // If an error is present, don't display the character counter or hint text
  // Priority is: error > character counter > hint text
  if (Boolean(errorText)) {
    return (
      <Caption
        color={theme.text.generic.negative}
        css={{ wordBreak: 'break-word' }}
      >
        {errorText}
      </Caption>
    );
  }

  if (Boolean(helperText) && !isShowCharCounter) {
    return (
      <Caption color={theme.text.generic.tertiary} css={{ display: 'flex' }}>
        {helperText}
      </Caption>
    );
  }

  const hasExceededCharCount = inputLength > inputMaxLength;
  const hasReachedCharCount = inputLength === inputMaxLength;

  if (isShowCharCounter) {
    const errorStyles = {
      // If the character count has been exceeded, show the text in red
      ...(hasExceededCharCount && {
        color: theme.text.generic.negative,
      }),
      // If the character count has been reached, pulse the text in red
      ...(hasReachedCharCount &&
        isCharacterCountPulseEnabled.current && {
          ...styles.inputCharacterCountPulseRedCSS,
        }),
    };
    return (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          justifyContent: 'right',
          alignItems: 'center',
        }}
      >
        <Caption
          css={{
            color: theme.text.generic.tertiary,
            ...errorStyles,
          }}
        >
          {!Boolean(message) &&
            `${inputLength}/${inputMaxLength} ${formatMessage({
              id: 'characters',
            }).toLocaleLowerCase()}`}

          {Boolean(message) &&
            message
              ?.replace('%inputLength', `${inputLength}`)
              .replace('%inputMaxLength', `${inputMaxLength}`)}
        </Caption>
      </Box>
    );
  }

  // If no error is present and the character counter is not needed, don't display anything
  return null;
};
